// fetch wrapper that attaches a fresh Bearer token and retries once after a 401 through the shared refresh gate.

import { forceRefresh, getValidAccessToken } from 'services/auth/token-store';

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;

const isRequest = (input: FetchInput): input is Request => typeof Request !== 'undefined' && input instanceof Request;

const send = (input: FetchInput, init: FetchInit, token: string): Promise<Response> => {
  const headers = new Headers(init.headers ?? (isRequest(input) ? input.headers : undefined));
  headers.set('Authorization', `Bearer ${token}`);
  // A Request body is single-use: always send a clone so the retry can re-send it.
  return fetch(isRequest(input) ? input.clone() : input, { ...init, headers });
};

export const authorizedFetch = async (input: FetchInput, init: FetchInit = {}): Promise<Response> => {
  const token = await getValidAccessToken();
  const response = await send(input, init, token);
  if (response.status !== 401) return response;

  // One retry with the refreshed token; a second 401 is returned to the caller without clearing the session.
  const refreshed = await forceRefresh(token);
  return send(input, init, refreshed);
};
